import { useEffect, useMemo, useRef, useState } from "react";
import consts from "shared/consts";
import Konva from "konva";
import { Html } from "react-konva-utils";
import tracking from "frontend/tracking";
import style from "./next-element-menu.module.css";
import cn from "classnames";
import { Floater } from "frontend/ui-components/floaters/floater";
import useOutsideRef from "frontend/utils/click-outside-handler";
import { Point } from "frontend/utils/math-utils";
import { FixedSizeList } from "react-window";
import useVirtualizedShapeLibrary from "./hooks/use-virtualized-shape-library";
import { ShapeLibraries, ShapeLibrary } from "../types";
import FallbackLibraries from "../fallback-data/libraries.json";
import { useCdn } from "frontend/hooks/caching/cdn/use-cdn";
import NextElementMenuRow from "./next-element-menu-row";
import { ShapeInfo } from "../side-panel/virtualized-list/virtualized-row";
import { SearchField } from "frontend/ui-components/search-field/search-field";

export type NextElementMenuItem = { elementType: string; elementData: Record<string, unknown>; elementInfo: ShapeInfo };

export type NextElementMenuOps = "copy" | "add";

export interface NextElementMenuProps {
  anchor: DOMRectReadOnly;
  originNode: Konva.Node | null;
  connectorId: string;
  connectorPoints: { start: Point; end: Point };
  onAddElement: (
    element: Konva.Node | NextElementMenuItem | null,
    connectorId: string,
    start: Point,
    end: Point,
    mode?: NextElementMenuOps
  ) => void;
  margin?: number;
}

export default function NextElementMenu({
  anchor,
  originNode,
  connectorId,
  connectorPoints,
  onAddElement,
  margin,
}: NextElementMenuProps) {
  const ref = useRef<HTMLDivElement>(null);
  const { start, end } = connectorPoints;

  useEffect(() => {
    tracking.trackAnalyticsEvent("next-shape-menu-shown", {
      category: consts.TRACKING_CATEGORY.CANVAS_ACTION,
    });
  }, []);

  useOutsideRef(ref, () => onAddElement(null, connectorId, start, end), { event: "mousedown" });

  const [searchTerm, setSearchTerm] = useState("");

  const { data: shapeLibraries, isLoading, error } = useCdn<ShapeLibraries>("/shape-libraries/libraries.json", "json");
  const library: ShapeLibrary = useMemo(() => {
    if (isLoading || error) return FallbackLibraries["basicShapes"];
    if (!originNode?.attrs.element.libraryPath) return shapeLibraries["basicShapes"];
    const libraryId = originNode.attrs.element.libraryPath.split(":")[0];
    return shapeLibraries[libraryId] ?? FallbackLibraries["basicShapes"];
  }, [isLoading, error, originNode?.attrs.element.libraryPath, shapeLibraries]);

  // In case of error or loading, use fallback library
  // But it won't be displayed because we handle these cases in the UI
  const rows = useVirtualizedShapeLibrary(library, searchTerm);

  function onMenuItemClick(elementType: string, elementData: Record<string, unknown>, elementInfo: ShapeInfo) {
    const element = {
      elementType,
      elementData,
      elementInfo,
    };
    onAddElement(element, connectorId, start, end, "add");
  }

  return (
    <Html transform={false}>
      <Floater
        ref={ref}
        side="bottom"
        arrowSize={0}
        margin={margin}
        relativeTo={anchor}
        className={cn(style.container, style.light)}
      >
        <div className={style.buttonContainer}>
          <button
            className={style.addSameObjectButton}
            onClick={() => onAddElement(originNode, connectorId, start, end, "copy")}
            disabled={!originNode}
          >
            Add same object
          </button>
        </div>

        {!error && (
          <div className={style.searchContainer}>
            <div className={style.searchFieldContainer}>
              <SearchField
                searchValue={searchTerm}
                setSearchValue={setSearchTerm}
                placeholder="Search"
                className={style.searchField}
                autoFocus
              />
            </div>
          </div>
        )}

        {!isLoading && !error && (
          <div className={style.listContainer}>
            <FixedSizeList
              height={200}
              width="100%"
              itemSize={38}
              itemCount={rows.length}
              itemData={{ rows, onMenuItemClick }}
            >
              {NextElementMenuRow}
            </FixedSizeList>
          </div>
        )}

        {isLoading && (
          <div className={style.skeletonContainer}>
            {Array.from({ length: 5 }).map((_, index) => (
              <div className={style.itemRow} key={`skeleton-row-${index}`}>
                {Array.from({ length: 4 }).map((_, index) => (
                  <div className={style.skeletonItem} key={`skeleton-item-${index}`} />
                ))}
              </div>
            ))}
          </div>
        )}

        {error && (
          <div className={style.errorContainer}>
            <span className={style.errorText}>
              Error loading
              <br /> elements
            </span>
          </div>
        )}
      </Floater>
    </Html>
  );
}
